import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {BaseFancyTextShapeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-fancy-text-shape-option';
import type {MorphType} from '@Libraries/fancy-text-library';
import {getDefaultMorphAmountFromType} from '@Libraries/fancy-text-library';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromFancyTextItem} from '@Hooks/poster-editor/useGetKeyFromFancyTextItem';

export function FancyTextShapeOption(): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const [isLoading, setIsLoading] = useState(false);
  const [isSubOptionLoading, setIsSubOptionLoading] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [tempState, setTempState] = useState<number>();
  const itemMorphAmount = useGetKeyFromFancyTextItem('morphAmount');
  const itemMorphType = useGetKeyFromFancyTextItem('morphType');

  if (!activeItem) {
    throw new Error('Active item is null');
  }

  if (!activeItem.isFancyText() || itemMorphAmount === undefined || itemMorphType === undefined) {
    throw new Error(`Expected active item to be of type Fancy Text, instead got ${activeItem.gitype}`);
  }

  const onUpdateShape = (clickedId: MorphType): void => {
    setIsLoading(true);
    setIsSubOptionLoading(true);
    void activeItem
      .updateFromObject({
        morphType: Number(clickedId),
        morphAmount: getDefaultMorphAmountFromType(Number(clickedId)),
      })
      .finally(() => {
        setIsLoading(false);
        setIsSubOptionLoading(false);
      });
  };

  const updateMorphAmount = (morphAmount: number): void => {
    if (!isSubOptionLoading) {
      setIsSubOptionLoading(true);
    }
    void activeItem
      .updateFromObject({
        morphAmount,
      })
      .finally(() => {
        setIsSubOptionLoading(false);
        setIsChanging(false);
      });
  };

  const onChangeMorphAmount = (amount: number): void => {
    if (!isChanging) {
      setIsChanging(true);
    }
    setTempState(amount);
  };
  const onChangeMorphAmountEnded = (amount: number): void => {
    updateMorphAmount(amount);
  };

  return (
    <BaseFancyTextShapeOption
      id="fancy-text-shape-option-sidebar"
      textShape={itemMorphType}
      updateShape={onUpdateShape}
      morphAmount={tempState && isChanging ? tempState : itemMorphAmount}
      updateMorphAmount={onChangeMorphAmount}
      updateMorphAmountEnded={onChangeMorphAmountEnded}
      isLoading={isLoading || isSubOptionLoading}
      isSubOptionDisabled={isSubOptionLoading}
    />
  );
}
